import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, AuthState } from '../types/auth'
import { authApi } from '../services/api/auth'
import { marketplaceApi } from '../services/api/marketplace'
import { exchangeApi } from '../services/api/exchange'
import { clearTokens, setAccessToken } from '../services/api/client'
import { supabase } from '../lib/supabase'

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>
  register: (userData: RegisterData) => Promise<void>
  logout: () => void
  verifyIdentity: (verificationData: IdentityVerificationData) => Promise<void>
  updateProfile: (profileData: Partial<User>) => Promise<void>
  refreshUser: () => Promise<void>
  isLoading: boolean
  mfaPending: boolean
  clearMfaPending: () => void
}

interface RegisterData {
  email: string
  password: string
  firstName?: string
  lastName?: string
  phone?: string
  address?: {
    city?: string
    state?: string
  }
}

interface IdentityVerificationData {
  ssn?: string
  dateOfBirth?: string
  addressStreet?: string
  addressZip?: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isIdentityVerified, setIsIdentityVerified] = useState(false)
  const [mfaPending, setMfaPending] = useState(false)

  const loadUserProfile = async () => {
    try {
      const userData = await authApi.getCurrentUser()
      setUser(userData)
      setIsAuthenticated(true)
      setIsIdentityVerified(userData.isIdentityVerified)
    } catch (error) {
      console.error('Failed to load user profile:', error)
      setUser(null)
      setIsAuthenticated(false)
      setIsIdentityVerified(false)
    }
  }

  const refreshUser = async () => {
    const userData = await authApi.getCurrentUser()
    setUser(userData)
    setIsIdentityVerified(userData.isIdentityVerified)
  }

  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession()
        if (sessionData.session) {
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession({
            refresh_token: sessionData.session.refresh_token,
          })
          if (refreshError || !refreshData.session) {
            clearTokens()
            setIsLoading(false)
            return
          }
          setAccessToken(refreshData.session.access_token)
          await loadUserProfile()
          const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
          const currentLevel = aalData?.currentLevel
          const nextLevel = aalData?.nextLevel
          if (currentLevel === 'aal1' && nextLevel === 'aal2') {
            setMfaPending(true)
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error)
        clearTokens()
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()

    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.access_token) {
        setAccessToken(session.access_token)
      } else if (event === 'SIGNED_OUT') {
        clearTokens()
      }
    })

    return () => {
      subscription.subscription.unsubscribe()
    }
  }, [])

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true)
      const response = await authApi.login(email, password)
      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        await supabase.auth.setSession({
          access_token: response.token,
          refresh_token: refreshToken,
        })
      }
      setUser(response.user)
      setIsAuthenticated(true)
      setIsIdentityVerified(response.user.isIdentityVerified)

      if (response.user.email === 'demo1@open1031.ai') {
        localStorage.removeItem(`hub_status_${response.user.id}`)
        const favoritesKey = `marketplace_favorites_${response.user.id}`
        const existingFavorites = localStorage.getItem(favoritesKey)
        if (!existingFavorites) {
          try {
            const { listings } = await marketplaceApi.listListings({ limit: 3 })
            const ids = listings.slice(0, 3).map((listing) => listing.id)
            localStorage.setItem(favoritesKey, JSON.stringify(ids))
          } catch {
          }
        }
        try {
          await exchangeApi.ensureDemoExchange()
          window.dispatchEvent(new CustomEvent('demo-exchange-ready'))
        } catch {
        }
      }

      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      if (
        aalData &&
        aalData.currentLevel === 'aal1' &&
        aalData.nextLevel === 'aal2'
      ) {
        setMfaPending(true)
      }
    } catch (error: any) {
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (userData: RegisterData) => {
    try {
      setIsLoading(true)
      const response = await authApi.register(userData)
      const refreshToken = localStorage.getItem('refresh_token')
      if (refreshToken) {
        await supabase.auth.setSession({
          access_token: response.token,
          refresh_token: refreshToken,
        })
      }
      setUser(response.user)
      setIsAuthenticated(true)
      setIsIdentityVerified(false)
    } catch (error) {
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const clearMfaPending = () => {
    setMfaPending(false)
  }

  const logout = async () => {
    await Promise.allSettled([
      authApi.logout(),
      supabase.auth.signOut(),
    ])
    const brandedSlug = sessionStorage.getItem('branded_slug')
    sessionStorage.removeItem('sales_hub_shown_demo1_session')
    sessionStorage.removeItem('branded_slug')
    localStorage.removeItem('demo_identity_verified')
    localStorage.removeItem('current_exchange_id')
    setUser(null)
    setIsAuthenticated(false)
    setIsIdentityVerified(false)
    setMfaPending(false)
    if (brandedSlug) {
      window.location.href = `/o/${brandedSlug}/login`
    } else {
      window.location.href = '/login'
    }
  }

  const verifyIdentity = async (verificationData: IdentityVerificationData) => {
    try {
      setIsLoading(true)
      const response = await authApi.verifyIdentity(verificationData)
      setUser(response.user)
      setIsIdentityVerified(response.user.isIdentityVerified)
    } catch (error) {
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const updateProfile = async (profileData: Partial<User>) => {
    try {
      setIsLoading(true)
      const updatedUser = await authApi.updateProfile(profileData)
      setUser(updatedUser)
    } catch (error) {
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isIdentityVerified,
    login,
    register,
    logout,
    verifyIdentity,
    updateProfile,
    refreshUser,
    isLoading,
    mfaPending,
    clearMfaPending,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
